import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import t from 'src/translations/main'
import { getApplicationType } from '../Onboarding/flowConfig'
import { getBaseApiHost } from 'src/components/Core/Utils/url_constants'
import useFetch from 'src/components/Core/Hooks/useFetch/useFetch'
import { useApplicationSelectedUpdate, useProspectsApplications, useProspectsApplicationsUpdate } from 'src/components/Core/Context/OnboardingContext'

import Popup from 'src/components/Atoms/Popup/Popup'
import Input from 'src/components/Atoms/Input/Input'
import Dropdown from 'src/components/Atoms/Dropdown/Dropdown'
import Font from 'src/components/Core/Typography/Font/Font'
import Spinner from 'src/components/Core/Loading/Spinner/Spinner'
import Flex from 'src/components/Layout/Flex/Flex'
import { Section } from 'src/components/Layout/Section/Section'
import TablePaginator from 'src/components/Sections/TablePaginator/TablePaginator'
import Icon from 'src/components/Core/Iconography/Icon/Icon'

import { GetButton, SearchBackground, SearchToolBar, SearchWrapper } from '../Reports/Statements/Statements.styles'
import { SearchTableSection } from './DocDashboard.styles'
import { GetApplicationsType, StepsStatusType } from '../Onboarding/types'
import { ChangeArgs } from 'src/components/Atoms/Dropdown/types'

const accTypeMap: Record<string, string> = {
  CorpAOF: 'Corporate',
  DeliverableAOF: 'Deliverable',
  IndividualAOF: 'Individual'
}

const statusMap: Record<string,string>  = {
  not_started: 'Not started',
  in_progress: 'Reverted/In progress',
  submitted: 'Submitted',
  aof_approved: 'AOF Approved',
  generating_signature_pack: 'Generating pack',
  awaiting_signature: 'Awaiting signatures',
  signature_received: 'Signature received',
  signature_rejected: 'Signature rejected',
  aof_completed: 'AOF Completed'
}

const tableColumns = [
  {
    Header: t('onboarding.docDashboard.table.col1'),
    accessor: 'col1',
    sortable: true,
    sortType: 'stringSort'
  },
  {
    Header: t('onboarding.docDashboard.table.col2'),
    accessor: 'col2',
    sortable: true,
    sortType: 'stringSort'
  },
  {
    Header: t('onboarding.docDashboard.table.col3'),
    accessor: 'col3',
    sortable: true,
    sortType: 'stringSort'
  },
  {
    Header: t('onboarding.docDashboard.table.col4'),
    accessor: 'col4',
    sortable: true,
    sortType: 'dateSort'
  },
  {
    Header: t('onboarding.docDashboard.table.col5'),
    accessor: 'col5',
    sortable: true,
    sortType: 'dateSort'
  },
  {
    Header: t('onboarding.docDashboard.table.col6'),
    accessor: 'col6',
    sortable: true,
    sortType: 'dateSort'
  },
  {
    Header: t('onboarding.docDashboard.table.col7'),
    accessor: 'col7',
    sortable: true,
    sortType: 'commentsCountSort'
  },
  {
    Header: t('onboarding.docDashboard.table.col8'),
    accessor: 'col8',
    sortable: true,
    sortType: 'commentsCountSort'
  },
]

const STATUS_OPTIONS: { label: string }[] = Object.keys(t('onboarding.docDashboard.status', { returnObjects: true })).map(key => ({ label: t(`onboarding.docDashboard.status.${key}`) }))

const ACCOUNT_TYPES: { label: string }[] = Object.keys(t('onboarding.docDashboard.accountTypes', { returnObjects: true })).map(key => ({ label: t(`onboarding.docDashboard.accountTypes.${key}`) }))

const optionsMap: Record<string, { label: string }[]> = {
  status: STATUS_OPTIONS,
  accountType: ACCOUNT_TYPES
}

const sortTypes = {
  // @ts-ignore:next-line
  stringSort: (a,b,id) => {
    const index = parseInt(id.split('col')[1]) - 1
    const stringA = a.cells[`${index}`]?.value?.props?.children
    const stringB = b.cells[`${index}`]?.value?.props?.children
    return stringB > stringA ? 1 : stringB < stringA ? -1 : 0
  },
  // @ts-ignore:next-line
  commentsCountSort: (a, b, id) => {
    const index = parseInt(id.split('col')[1]) - 1
    if(index === 7) return parseInt(a.original[`${id}`]?.props?.children[1]?.props?.children?.split('/')[0]) > parseInt(b.original[`${id}`]?.props?.children[1]?.props?.children?.split('/')[0]) ? 1 : -1
    return parseInt(a.cells[`${index}`]?.value?.props?.children?.props?.children[1]?.props?.children?.split('/')[0]) < parseInt(b.cells[`${index}`]?.value?.props?.children?.props?.children[1]?.props?.children?.split('/')[0]) ? 1 : -1
  },
  // @ts-ignore:next-line
  dateSort: (a, b, id) => {
    const index = parseInt(id.split('col')[1]) - 1
    const dateA = a.cells[`${index}`]?.value?.props?.children
    const dateB = b.cells[`${index}`]?.value?.props?.children
    return dateB > dateA ? 1 : dateB < dateA ? -1 : 0
  }
}

const DocDashboard = () => {
  const [applications, setApplications] = useState([])
  const [hasLoaded, setHasLoaded] = useState(false)
  const [hasError, setError] = useState({error: false, message: ''})
  const [searchValues, setSearchValues] = useState({
    prospectName: '',
    status: {value: '', index: null},
    accountType: {value: '', index: null}
  })
  const [currentPage, setCurrentPage] = useState(1)

  const allApplications: GetApplicationsType[] = useProspectsApplications()
  const setApplicationSelected = useApplicationSelectedUpdate()
  const selectedApplicationUpdate = useProspectsApplicationsUpdate()

  const { sendRequest, isLoading } = useFetch()

  const navigate = useNavigate()

  const filteredApplications = (data: GetApplicationsType[]) => data.filter((application: GetApplicationsType) => {
    return (application.status === 'in_progress' && application.stepsStatus.some((step: StepsStatusType) => step.commentsCount > 0)) || !['not_started', 'in_progress','in progress'].includes(application.status)
  })
    .sort((a: GetApplicationsType,b: GetApplicationsType) => b.lastCommentDate > a.lastCommentDate ? 1 : b.lastCommentDate < a.lastCommentDate ? -1 : 0)

  useEffect(() => {
    sendRequest({
      scope: 'onboarding',
      url: import.meta.env.VITE_MOCK_DATA !== 'true'
        ? `${getBaseApiHost()}/AccountOpeningForms/getapplications`
        : `${window.location.origin}/mock/getApp.json`,
    },
    data => {
      if(data[0].permission !== 'documentation') {
        navigate('/error/internalProblem')
        return
      }
      setApplications(filteredApplications(data))
      setHasLoaded(true)
      selectedApplicationUpdate(data)
    },
    () => {
      setError({error: true, message: t('onboarding.docDashboard.errorFetching')})
      setHasLoaded(true)
    }
    )
  },[])

  const updateSearchParams = (e: React.ChangeEvent<HTMLInputElement> | ChangeArgs, param: string) => {
    setSearchValues(prev => ({
      ...prev,
      // @ts-ignore:next-line
      [`${param}`]: param === 'prospectName' ? e.target.value : { value: optionsMap[`${param}`][`${e.value as number}`].label, index: e.value }
    }))
  }

  const filterData = () => {
    let filterProspectName = applications.filter(application => {
      return application.prospectName.toLowerCase().includes(searchValues.prospectName.toLowerCase())
    })

    if(searchValues.status.value) {
      filterProspectName = filterProspectName.filter(application => {
        const keyToFind = Object.entries(statusMap).find(item => item[1] === searchValues.status.value)[0]
        return application.status.toLowerCase().includes(keyToFind)
      })
    }

    if(searchValues.accountType.value) {
      filterProspectName = filterProspectName.filter(application => {
        const keyToFind = Object.entries(accTypeMap).find(item => item[1] === searchValues.accountType.value)[0]
        return application.applicationType.includes(keyToFind)
      })
    }
    setCurrentPage(1)
    setApplications(filterProspectName)
  }

  const clearAllFilters = () => {
    setSearchValues({
      prospectName: '',
      status: {value: '', index: null},
      accountType: {value: '', index: null}
    })
    setApplications(filteredApplications(allApplications))
  }

  const getCommentsCounts = (stepsStatus: StepsStatusType[]) => {
    const unreadComments = stepsStatus?.reduce?.((acc: number, cur: StepsStatusType) => acc + cur.unreadCommentsCount, 0)
    const commentsCount = stepsStatus?.reduce?.((acc: number, cur: StepsStatusType) => acc + cur.commentsCount, 0)

    return (
      <>
        <Icon name='addComment' size='s' color={unreadComments > 0 ? 'ultraViolet' : 'backgroundDark'}/>
        <Font bold oneLine>{`${unreadComments ?? '--'}/${commentsCount ?? '--'}`}</Font>
      </>)
  }

  const goToApplication = (obj: GetApplicationsType) => {
    setApplicationSelected(allApplications.findIndex(application => application.applicationId === obj.applicationId))
    navigate(`/onboarding/${getApplicationType(obj.applicationType)}/${obj.progressIndex || 1}`, { replace: window.location.pathname.includes('loading') })
  }

  const dateFormatOptions: Intl.DateTimeFormatOptions = {
    year: '2-digit',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }
  const getFormattedDate = (date: Date) => {
    if(!date) return
    return new Intl.DateTimeFormat(undefined, dateFormatOptions).format(new Date(date))
  }

  const overviewTableData =
    applications
      ?.map(obj => ({
        col1: <Font bold oneLine onClick={() => goToApplication(obj)}>{obj.prospectName || '--'}</Font>,
        col2: <Font bold oneLine>{accTypeMap[`${obj.applicationType}`] || '--'}</Font>,
        col3: <Font bold oneLine>{statusMap[`${obj.status?.toLowerCase()}`] || '--'}</Font>,
        col4: <Font bold oneLine>{getFormattedDate(obj.submittedDate) || '--'}</Font>,
        col5: <Font bold oneLine>{getFormattedDate(obj.lastCommentDate) || '--'}</Font>,
        col6: <Font bold oneLine>{getFormattedDate(obj.lastMessageDate) || '--'}</Font>,
        col7: <Flex flexDirection='row' justifyContent='flex-start' gap='8px'>
          {getCommentsCounts(obj.stepsStatus) || '--'}
        </Flex>,
        col8: <Flex flexDirection='row' justifyContent='flex-start' gap='8px'>
          <Icon name='addComment' size='s' color={obj.unreadCommentsCount > 0 ? 'ultraViolet' : 'backgroundDark'}/>
          <Font bold oneLine>{`${obj.unreadCommentsCount ?? '--'}/${obj.commentsCount ?? '--'}`}</Font>
        </Flex>
      }))

  if(!hasLoaded || isLoading) return <Spinner removeFullscreen/>

  const getTableData = () => {
    const numberOfPages = Math.ceil(overviewTableData.length / 20)
    if(numberOfPages === 0) return [[]]
    const basePagesArray = Array.from({ length: numberOfPages }, (item, index) => index)
    const dataPagesArray = basePagesArray.map((data, index) => overviewTableData.slice(index * 20, (index * 20) + 20))
    return dataPagesArray
  }

  return (
    <>
      <Section addBottomBorder>
        <Font tag='h2'>{t('onboarding.docDashboard.title')}</Font>
      </Section>
      <SearchTableSection removeMargin>
        <SearchWrapper>
          <SearchBackground>
            <SearchToolBar flexWrap='wrap'>
              <Input
                label={t('onboarding.docDashboard.search.prospectName.label')}
                placeholder={t('onboarding.docDashboard.search.prospectName.label')}
                fixWidth
                onChange={e => updateSearchParams(e, 'prospectName')}
                value={searchValues.prospectName}
              />
              <Dropdown
                fixWidth
                label={t('onboarding.docDashboard.search.status.label')}
                placeholder={t('onboarding.docDashboard.search.status.label')}
                options={optionsMap?.status}
                onChange={e => updateSearchParams(e, 'status')}
                outsideIndexSelected={searchValues.status.index === '0' ? 0 : (parseInt(searchValues.status.index) || null)}
              />
              <Dropdown
                fixWidth
                label={t('onboarding.docDashboard.search.accountType.label')}
                placeholder={t('onboarding.docDashboard.search.accountType.label')}
                options={optionsMap?.accountType}
                onChange={e => updateSearchParams(e, 'accountType')}
                outsideIndexSelected={searchValues.accountType.index === '0' ? 0 : (parseInt(searchValues.accountType.index) || null)}
              />
              <GetButton
                version={1}
                label={t('onboarding.docDashboard.search.getData')}
                onClick={filterData}
                overflow='visible'
              />
              <GetButton
                version={1}
                label={t('onboarding.docDashboard.search.clearAllFilters')}
                onClick={clearAllFilters}
                overflow='visible'
              />
            </SearchToolBar>
          </SearchBackground>
        </SearchWrapper>
        <TablePaginator
          show={true}
          currentPage={currentPage}
          totalItems={overviewTableData.length}
          tableColumns={tableColumns}
          tableData={getTableData()}
          updateCurrentPage={page => setCurrentPage(page)}
          limitPerPage={20}
          sortTypes={sortTypes}
        />
      </SearchTableSection>
      {
        hasError.error &&
        <Popup
          label={hasError.message}
          popupCloseHelperFunction={() => {setError({error: false, message: ''})}}
          click={() => setError({error: false, message: ''})}
          type='error'
        />
      }
    </>
  )
}

export default DocDashboard
